// src/views/words-lists/components/Toolbar.tsx
import React, { memo } from 'react';
import { Button, Input, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { NodeExpandOutlined, NodeCollapseOutlined } from '@ant-design/icons';
import {
  TbFolderPlus,
  TbPlaylistAdd,
  TbListCheck,
  TbX,
  TbTrash,
} from 'react-icons/tb';
import styles from '../FolderTreeView.module.less';

interface ToolbarProps {
  onCreateFolder: () => void;
  onCreateList: () => void;
  onToggleExpand: () => void;
  isExpanded: boolean;
  isBulkMode: boolean;
  onToggleBulkMode: () => void;
  onBulkDelete: () => void;
  selectedCount: number;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  isCreating: boolean;
  isDeleting: boolean;
}

export const Toolbar = memo<ToolbarProps>(({
  onCreateFolder,
  onCreateList,
  onToggleExpand,
  isExpanded,
  isBulkMode,
  onToggleBulkMode,
  onBulkDelete,
  selectedCount,
  searchQuery,
  onSearchChange,
  isCreating,
  isDeleting,
}) => {
  const { t } = useTranslation();

  return (
    <div className={styles.toolbar}>
      <div className={styles.toolbarLeft}>
        <Tooltip title={isExpanded ? t('folders:toolbar.collapseAll') : t('folders:toolbar.expandAll')}>
          <Button
            icon={isExpanded ? <NodeCollapseOutlined /> : <NodeExpandOutlined />}
            onClick={onToggleExpand}
            style={{ minWidth: 50 }}
          />
        </Tooltip>

        {/* Na telefonie przyciski pokazują same ikony: napis (.buttonLabel) chowają style, a pokazuje go dymek
            (.labelTooltip — tylko na telefonie). „Usuń” zostawia liczbę zaznaczonych.
            Ikona stoi przy prawej krawędzi, więc idzie jako dziecko przycisku, nie przez `icon`
            (antd stawia ją przed napisem) */}
        <Tooltip title={t('folders:toolbar.newFolder')} overlayClassName={styles.labelTooltip}>
          <Button
            // type="primary"
            onClick={onCreateFolder}
            loading={isCreating}
          >
            <span className={styles.buttonLabel}>{t('folders:toolbar.newFolder')}</span>
            <TbFolderPlus className={styles.toolbarIcon} />
          </Button>
        </Tooltip>

        <Tooltip title={t('folders:toolbar.newList')} overlayClassName={styles.labelTooltip}>
          <Button onClick={onCreateList}>
            <span className={styles.buttonLabel}>{t('folders:toolbar.newList')}</span>
            <TbPlaylistAdd className={styles.toolbarIcon} />
          </Button>
        </Tooltip>

        {isBulkMode ? (
          <>
            <Tooltip title={t('folders:toolbar.cancelSelection')} overlayClassName={styles.labelTooltip}>
              <Button onClick={onToggleBulkMode}>
                <span className={styles.buttonLabel}>{t('folders:toolbar.cancelSelection')}</span>
                <TbX className={styles.toolbarIcon} />
              </Button>
            </Tooltip>
            <Button
              danger
              onClick={onBulkDelete}
              disabled={selectedCount === 0}
              loading={isDeleting}
            >
              {/* Napis z liczbą w jednym elemencie — osobno dostałyby między sobą odstęp ikony zamiast spacji */}
              <span><span className={styles.buttonLabel}>{t('common:buttons.delete')}</span> ({selectedCount})</span>
              <TbTrash className={styles.toolbarIcon} />
            </Button>
          </>
        ) : (
          <Tooltip title={t('folders:toolbar.bulkModeTooltip')}>
            <Button onClick={onToggleBulkMode}>
              <span className={styles.buttonLabel}>{t('folders:toolbar.deleteMany')}</span>
              <TbListCheck className={styles.toolbarIcon} />
            </Button>
          </Tooltip>
        )}
      </div>

      <Input.Search
        className={styles.searchInput}
        placeholder={t('folders:toolbar.search')}
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
        allowClear
      />
    </div>
  );
});

Toolbar.displayName = 'Toolbar';
